// Test client for the MedicalFX video service, built on the official LiveKit SDK (livekit-client).
// Also the reference for ACCURACY (React) / MFX (Flutter): the same calls exist in their SDKs.
//   - token: from your backend (here: /api/dev/token), one token per user per room
//   - LiveKit: room.connect(url, token), publish camera/mic/screen, render subscribed tracks
//   - chat: text streams on topic "lk.chat"; shared files: "medicalfx.file"; direct files: byte streams
//   - recording: POST /api/rooms/{room}/recording; state: room.isRecording
import {
  ConnectionQuality,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
  VideoPresets,
} from 'livekit-client';

const $ = (selector) => document.querySelector(selector);

const TOPIC_CHAT = 'lk.chat';
const TOPIC_FILE = 'medicalfx.file';
const TOPIC_DIRECT_FILE = 'medicalfx.direct-file';

const state = {
  token: null,
  roomName: null,
  identity: null,
  room: null,
  synthetic: null,
  tiles: new Map(), // `${identity}:${source}` -> tile
  prevBytes: new Map(),
  timers: [],
};
window.telemed = state; // for debugging from the console

// ------------------------------------------------------------------ utils

function log(...parts) {
  const line = `${new Date().toLocaleTimeString()}  ${parts.map((p) => (typeof p === 'string' ? p : JSON.stringify(p))).join(' ')}`;
  const el = $('#log');
  el.textContent = `${line}\n${el.textContent}`.slice(0, 20_000);
  console.log(...parts);
}

const storage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* private mode */
    }
  },
};

const formatSize = (bytes) =>
  bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} МБ` : `${Math.max(1, Math.round(bytes / 1024))} КБ`;

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement('a'), { href: url, download: name }).click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

async function api(path, options = {}) {
  const res = await fetch(path, {
    ...options,
    headers: { ...(options.headers ?? {}), authorization: `Bearer ${state.token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  }
  return res;
}

// ------------------------------------------------------------------ synthetic media (no camera)

function syntheticTracks(label) {
  const canvas = Object.assign(document.createElement('canvas'), { width: 1280, height: 720 });
  const ctx = canvas.getContext('2d');
  const hue = Math.floor(Math.random() * 360);
  let frame = 0;
  const draw = () => {
    frame++;
    ctx.fillStyle = `hsl(${hue} 45% 22%)`;
    ctx.fillRect(0, 0, 1280, 720);
    ctx.fillStyle = `hsl(${hue} 80% 60%)`;
    ctx.beginPath();
    ctx.arc(640 + Math.cos(frame / 20) * 440, 400 + Math.sin(frame / 13) * 180, 60, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 60px system-ui';
    ctx.fillText(label, 48, 100);
    // Sender clock: compare with the viewer clock on the tile to see end-to-end latency.
    ctx.font = 'bold 88px ui-monospace, monospace';
    ctx.fillText(new Date().toISOString().slice(17, 23), 48, 660);
  };
  const timer = setInterval(draw, 40);
  const video = canvas.captureStream(25).getVideoTracks()[0];

  const audioContext = new AudioContext();
  const osc = audioContext.createOscillator();
  osc.frequency.value = 300 + Math.random() * 500;
  const gain = audioContext.createGain();
  gain.gain.value = 0;
  const dest = audioContext.createMediaStreamDestination();
  osc.connect(gain).connect(dest);
  osc.start();
  const beep = setInterval(() => {
    gain.gain.setValueAtTime(0.15, audioContext.currentTime);
    gain.gain.setValueAtTime(0, audioContext.currentTime + 0.12);
  }, 1000);

  return {
    video: new LocalVideoTrack(video, undefined, false),
    audio: new LocalAudioTrack(dest.stream.getAudioTracks()[0], undefined, false),
    stop() {
      clearInterval(timer);
      clearInterval(beep);
      audioContext.close();
    },
  };
}

// ------------------------------------------------------------------ tiles

function tileKey(participant, source) {
  return `${participant.identity}:${source === Track.Source.ScreenShare ? 'screen' : 'camera'}`;
}

function ensureTile(participant, source = Track.Source.Camera) {
  const key = tileKey(participant, source);
  let tile = state.tiles.get(key);
  if (tile) return tile;
  const el = document.createElement('div');
  el.className = 'tile';
  el.innerHTML =
    '<video autoplay playsinline muted></video><span class="label"></span><span class="clock"></span>' +
    '<span class="stats"></span><span class="actions"></span>';
  const role = participant.attributes?.role ? ` · ${participant.attributes.role}` : '';
  const screen = source === Track.Source.ScreenShare ? ' — экран' : '';
  el.querySelector('.label').textContent = participant.isLocal
    ? `${participant.name || participant.identity} (вы)${screen}`
    : `${participant.name || participant.identity}${role}${screen}`;
  $('#grid').append(el);
  tile = { el, video: el.querySelector('video'), stats: el.querySelector('.stats'), clock: el.querySelector('.clock'), participant, source };
  state.tiles.set(key, tile);
  renderTileActions(tile);
  return tile;
}

// Doctor controls on remote tiles: admit from the lobby, remove from the room.
function renderTileActions(tile) {
  const box = tile.el.querySelector('.actions');
  box.textContent = '';
  const p = tile.participant;
  const inLobby = p.attributes?.lobby === '1';
  tile.el.classList.toggle('lobby', inLobby);
  if (p.isLocal || tile.source === Track.Source.ScreenShare || state.role !== 'doctor') return;
  const action = (label, path, done) => {
    const button = Object.assign(document.createElement('button'), { type: 'button', textContent: label });
    button.onclick = async () => {
      button.disabled = true;
      try {
        await api(`/api/rooms/${state.roomName}/participants/${encodeURIComponent(p.identity)}/${path}`, { method: 'POST' });
        log(done);
      } catch (err) {
        log(`${label}:`, err.message);
        button.disabled = false;
      }
    };
    box.append(button);
  };
  if (inLobby) action('Впустить', 'admit', `${p.name || p.identity} впущен(а)`);
  action('Удалить', 'remove', `${p.name || p.identity} удалён(а) из комнаты`);
}

async function publishLocalMedia(name) {
  const lp = state.room.localParticipant;
  if ($('#synthetic').checked) {
    state.synthetic ??= syntheticTracks(name);
    await lp.publishTrack(state.synthetic.video, { source: Track.Source.Camera });
    await lp.publishTrack(state.synthetic.audio, { source: Track.Source.Microphone });
  } else {
    await lp.setMicrophoneEnabled(true).catch((err) => log('Микрофон недоступен:', err.message));
    await lp.setCameraEnabled(true).catch((err) => log('Камера недоступна:', err.message));
  }
}

function removeTile(key) {
  state.tiles.get(key)?.el.remove();
  state.tiles.delete(key);
}

function removeParticipantTiles(participant) {
  for (const key of [...state.tiles.keys()]) if (key.startsWith(`${participant.identity}:`)) removeTile(key);
}

function attachTrack(track, participant) {
  if (track.kind === Track.Kind.Video) {
    const tile = ensureTile(participant, track.source);
    track.attach(tile.video);
    tile.el.classList.toggle('cam-off', track.isMuted);
  } else if (!participant.isLocal) {
    const audio = track.attach();
    audio.muted = $('#mutePlayback').checked; // several test tabs on one computer
    audio.dataset.identity = participant.identity;
    document.body.append(audio);
  }
}

function detachTrack(track, participant) {
  for (const el of track.detach()) if (el.tagName === 'AUDIO') el.remove();
  if (track.source === Track.Source.ScreenShare) removeTile(tileKey(participant, track.source));
}

// ------------------------------------------------------------------ stats

function summarize(report) {
  const byId = new Map();
  report.forEach((s) => byId.set(s.id, s));
  const out = {};
  let pair;
  report.forEach((s) => {
    if ((s.type === 'inbound-rtp' || s.type === 'outbound-rtp') && s.kind === 'video') {
      if (!out.w || (s.frameWidth ?? 0) > out.w) {
        Object.assign(out, { w: s.frameWidth, h: s.frameHeight, fps: s.framesPerSecond });
      }
      out.bytes = (out.bytes ?? 0) + (s.bytesReceived ?? s.bytesSent ?? 0);
    }
    if (s.type === 'transport' && s.selectedCandidatePairId) pair = byId.get(s.selectedCandidatePairId);
    if (!pair && s.type === 'candidate-pair' && s.nominated && s.state === 'succeeded') pair = s;
  });
  if (pair) {
    if (pair.currentRoundTripTime !== undefined) out.rttMs = Math.round(pair.currentRoundTripTime * 1000);
    const local = byId.get(pair.localCandidateId);
    if (local) out.path = local.candidateType === 'relay' ? `relay/${local.relayProtocol ?? local.protocol}` : `${local.candidateType}/${local.protocol}`;
  }
  return out;
}

const QUALITY = {
  [ConnectionQuality.Excellent]: 'отлично',
  [ConnectionQuality.Good]: 'хорошо',
  [ConnectionQuality.Poor]: 'плохо',
  [ConnectionQuality.Lost]: 'потеряно',
};

async function updateStats() {
  for (const [key, tile] of state.tiles) {
    const publication = tile.participant.getTrackPublication(tile.source);
    const track = publication?.track;
    if (!track) continue;
    try {
      const report = await track.getRTCStatsReport();
      if (!report) continue;
      const s = summarize(report);
      const prev = state.prevBytes.get(key) ?? 0;
      const kbps = prev && s.bytes ? Math.round(((s.bytes - prev) * 8) / 2000) : 0;
      state.prevBytes.set(key, s.bytes ?? 0);
      tile.stats.textContent = [
        s.w ? `${s.w}x${s.h}@${Math.round(s.fps ?? 0)}` : null,
        `${kbps} kbps`,
        s.rttMs !== undefined ? `RTT ${s.rttMs}ms` : null,
        s.path,
        QUALITY[tile.participant.connectionQuality],
      ]
        .filter(Boolean)
        .join(' · ');
    } catch {
      /* track just ended */
    }
  }
}

// ------------------------------------------------------------------ chat & files

function addMessage({ from, ts, text, file, blob }) {
  const el = document.createElement('div');
  el.className = 'msg';
  const head = document.createElement('div');
  head.innerHTML = '<b></b><time></time>';
  head.querySelector('b').textContent = from;
  head.querySelector('time').textContent = new Date(ts).toLocaleTimeString();
  el.append(head);
  if (text) el.append(Object.assign(document.createElement('div'), { textContent: text }));
  if (file) {
    const a = Object.assign(document.createElement('a'), { textContent: `${file.name} (${formatSize(file.size)})` });
    a.onclick = async () => {
      try {
        if (blob) return downloadBlob(blob, file.name);
        const res = await api(`/api/rooms/${state.roomName}/files/${file.id}`);
        downloadBlob(await res.blob(), file.name);
      } catch (err) {
        log('Скачивание:', err.message);
      }
    };
    el.append(a);
  }
  $('#messages').append(el);
  $('#messages').scrollTop = 1e9;
}

function senderName(identity) {
  const room = state.room;
  if (!room) return identity;
  if (identity === room.localParticipant.identity) return room.localParticipant.name || identity;
  const p = room.remoteParticipants.get(identity);
  return p?.name || identity;
}

function registerDataHandlers(room) {
  room.registerTextStreamHandler(TOPIC_CHAT, async (reader, info) => {
    addMessage({ from: senderName(info.identity), ts: Date.now(), text: await reader.readAll() });
  });
  room.registerTextStreamHandler(TOPIC_FILE, async (reader, info) => {
    const file = JSON.parse(await reader.readAll());
    addMessage({ from: senderName(info.identity), ts: Date.now(), file });
  });
  room.registerByteStreamHandler(TOPIC_DIRECT_FILE, async (reader, info) => {
    const chunks = await reader.readAll();
    const blob = new Blob(chunks, { type: reader.info.mimeType });
    addMessage({ from: senderName(info.identity), ts: Date.now(), file: { name: reader.info.name, size: blob.size }, blob });
  });
}

async function sendChat(text) {
  await state.room.localParticipant.sendText(text, { topic: TOPIC_CHAT });
  addMessage({ from: `${state.room.localParticipant.name} (вы)`, ts: Date.now(), text });
}

async function uploadFile(file) {
  const form = new FormData();
  form.append('file', file, file.name);
  log(`Загрузка ${file.name}…`);
  const meta = await (await api(`/api/rooms/${state.roomName}/files`, { method: 'POST', body: form })).json();
  await state.room.localParticipant.sendText(JSON.stringify(meta), { topic: TOPIC_FILE });
  addMessage({ from: `${state.room.localParticipant.name} (вы)`, ts: Date.now(), file: meta });
}

async function sendDirectFile(file) {
  await state.room.localParticipant.sendFile(file, { topic: TOPIC_DIRECT_FILE });
  addMessage({ from: `${state.room.localParticipant.name} (вы)`, ts: Date.now(), text: `Файл «${file.name}» отправлен напрямую` });
}

// ------------------------------------------------------------------ recording

function setRecording(active) {
  $('#recBadge').hidden = !active;
  $('#recBtn').textContent = active ? 'Остановить запись' : 'Начать запись';
}

async function toggleRecording() {
  const action = state.room.isRecording ? 'stop' : 'start';
  $('#recBtn').disabled = true;
  try {
    await api(`/api/rooms/${state.roomName}/recording`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ action }),
    });
    log(action === 'start' ? 'Запись запущена' : 'Запись остановлена, файл собирается на сервере');
  } catch (err) {
    log('Запись:', err.message);
  } finally {
    $('#recBtn').disabled = false;
  }
}

async function showRecordings() {
  const { recordings } = await (await api(`/api/rooms/${state.roomName}/recordings`)).json();
  const box = $('#recordings');
  box.textContent = recordings.length ? '' : 'Пока нет записей';
  const statusText = { done: 'готово', running: 'собирается…', pending: 'в очереди', failed: 'ошибка', disabled: 'без сборки', empty: 'пусто: никто не включил камеру или микрофон' };
  for (const rec of recordings) {
    const row = document.createElement('div');
    row.textContent = `${new Date(rec.startedAt).toLocaleString()} · ${Math.round(rec.durationMs / 1000)} с · дорожек: ${rec.tracks.length} · ${statusText[rec.composition?.status] ?? rec.composition?.status} `;
    if (rec.composition?.status === 'done') {
      const a = Object.assign(document.createElement('a'), { textContent: 'скачать MP4' });
      a.onclick = async () => {
        const res = await api(`/api/rooms/${state.roomName}/recordings/${rec.id}/${rec.composition.file}`);
        downloadBlob(await res.blob(), `${state.roomName}-${rec.id}.mp4`);
      };
      row.append(a);
    }
    box.append(row);
  }
  $('#recordingsPanel').hidden = false;
}

// ------------------------------------------------------------------ join / leave

function setStatus(text, kind = '') {
  const badge = $('#modeBadge');
  badge.className = `badge ${kind}`;
  badge.textContent = text;
}

function setInCall(inCall, role) {
  $('#joinForm').hidden = inCall;
  $('#call').hidden = !inCall;
  for (const el of ['#chatInput', '#fileInput', '#directFileInput', '#chatForm button']) $(el).disabled = !inCall;
  $('#recBtn').hidden = !inCall || role !== 'doctor';
  $('#recListBtn').hidden = !inCall || role !== 'doctor';
}

function wireRoom(room) {
  room
    .on(RoomEvent.ParticipantConnected, (p) => {
      ensureTile(p);
      log(`${p.name || p.identity} подключился`);
    })
    .on(RoomEvent.ParticipantDisconnected, (p) => {
      removeParticipantTiles(p);
      log(`${p.name || p.identity} вышел`);
    })
    .on(RoomEvent.TrackSubscribed, (track, _pub, p) => attachTrack(track, p))
    .on(RoomEvent.TrackUnsubscribed, (track, _pub, p) => detachTrack(track, p))
    .on(RoomEvent.LocalTrackPublished, (pub, p) => pub.track && attachTrack(pub.track, p))
    .on(RoomEvent.LocalTrackUnpublished, (pub, p) => pub.track && detachTrack(pub.track, p))
    .on(RoomEvent.TrackMuted, (pub, p) => {
      if (pub.kind === Track.Kind.Video) state.tiles.get(tileKey(p, pub.source))?.el.classList.add('cam-off');
    })
    .on(RoomEvent.TrackUnmuted, (pub, p) => {
      if (pub.kind === Track.Kind.Video) state.tiles.get(tileKey(p, pub.source))?.el.classList.remove('cam-off');
    })
    .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      const active = new Set(speakers.map((s) => s.identity));
      for (const [key, tile] of state.tiles) tile.el.classList.toggle('speaking', active.has(key.split(':')[0]) && key.endsWith(':camera'));
    })
    .on(RoomEvent.ParticipantAttributesChanged, (_changed, p) => {
      for (const tile of state.tiles.values()) if (tile.participant.identity === p.identity) renderTileActions(tile);
    })
    .on(RoomEvent.ParticipantPermissionsChanged, async (_prev, p) => {
      // Admitted from the lobby: the server granted publish/subscribe rights.
      if (p.isLocal && p.permissions?.canPublish && state.waiting) {
        state.waiting = false;
        setStatus('в комнате', 'sfu');
        log('Врач впустил вас в комнату');
        await publishLocalMedia(p.name || p.identity).catch((err) => log('Публикация:', err.message));
      }
    })
    .on(RoomEvent.RecordingStatusChanged, (recording) => {
      setRecording(recording);
      log(recording ? 'Идёт запись' : 'Запись выключена');
    })
    .on(RoomEvent.Reconnecting, () => {
      setStatus('переподключение…', 'rec');
      log('Связь потеряна, переподключение…');
    })
    .on(RoomEvent.Reconnected, () => {
      setStatus('в комнате', 'sfu');
      log('Связь восстановлена');
    })
    .on(RoomEvent.Disconnected, (reason) => {
      log(`Отключено (${reason ?? '—'})`);
      cleanup();
    });
}

async function join(event) {
  event.preventDefault();
  const accessKey = $('#accessKey').value;
  const name = $('#name').value.trim();
  const role = $('#role').value;
  const roomName = $('#room').value.trim();
  storage.set('telemed.accessKey', accessKey);
  storage.set('telemed.name', name);
  $('#joinForm button').disabled = true;
  $('#joinError').hidden = true;

  try {
    const res = await fetch('/api/dev/token', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ accessKey, name, role, room: roomName, lobby: $('#lobby').checked }),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.error?.message ?? `HTTP ${res.status}`);
    state.token = body.token;
    state.roomName = roomName;
    state.role = role;

    const room = new Room({
      // Subscribe to the layer that fits the rendered size; hidden tiles get no video at all.
      // The test toggle keeps video flowing in background tabs.
      adaptiveStream: !$('#noAdaptive').checked,
      dynacast: true, // stop encoding layers nobody watches
      publishDefaults: {
        simulcast: true,
        videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
        videoCodec: 'vp8',
        red: true,
        dtx: true,
      },
      videoCaptureDefaults: { resolution: VideoPresets.h720.resolution },
      audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    state.room = room;
    wireRoom(room);
    registerDataHandlers(room);

    setStatus('подключение…');
    const rtcConfig = $('#ice').value === 'relay' ? { iceTransportPolicy: 'relay' } : undefined;
    await room.connect(body.url, state.token, { rtcConfig });
    state.identity = room.localParticipant.identity;
    setInCall(true, role);
    setStatus('в комнате', 'sfu');
    setRecording(room.isRecording);
    log(`Вошли в «${roomName}», участников: ${room.numParticipants}`);

    for (const p of room.remoteParticipants.values()) ensureTile(p);
    ensureTile(room.localParticipant);

    if (room.localParticipant.permissions?.canPublish === false) {
      state.waiting = true;
      setStatus('ожидание в лобби', 'rec');
      log('Вы в лобби: врач увидит вас и впустит');
    } else {
      await publishLocalMedia(name);
    }

    state.timers.push(setInterval(() => updateStats().catch(() => {}), 2000));
    state.timers.push(
      setInterval(() => {
        const now = new Date().toISOString().slice(17, 23);
        for (const tile of state.tiles.values()) if (!tile.participant.isLocal) tile.clock.textContent = now;
      }, 50),
    );
  } catch (err) {
    log('Ошибка входа:', err.message);
    // The log panel is below the fold on phones: show the reason right under the button too.
    Object.assign($('#joinError'), { textContent: `Не удалось войти: ${err.message}`, hidden: false });
    cleanup();
  } finally {
    $('#joinForm button').disabled = false;
  }
}

function cleanup() {
  for (const timer of state.timers) clearInterval(timer);
  state.timers = [];
  for (const key of [...state.tiles.keys()]) removeTile(key);
  for (const audio of document.querySelectorAll('audio[data-identity]')) audio.remove();
  state.synthetic?.stop();
  state.synthetic = null;
  const room = state.room;
  state.room = null;
  room?.removeAllListeners();
  room?.disconnect();
  setRecording(false);
  setStatus('не подключено');
  setInCall(false);
}

// ------------------------------------------------------------------ UI wiring

$('#accessKey').value = storage.get('telemed.accessKey') ?? '';
$('#name').value = storage.get('telemed.name') ?? `Участник ${Math.floor(Math.random() * 900 + 100)}`;
$('#joinForm').addEventListener('submit', join);
$('#leaveBtn').onclick = () => cleanup();

$('#micBtn').onclick = async () => {
  const lp = state.room.localParticipant;
  const enabled = !lp.isMicrophoneEnabled;
  await lp.setMicrophoneEnabled(enabled).catch((e) => log(e.message));
  $('#micBtn').textContent = `Микрофон: ${enabled ? 'вкл' : 'выкл'}`;
  $('#micBtn').classList.toggle('off', !enabled);
};

$('#camBtn').onclick = async () => {
  const lp = state.room.localParticipant;
  const enabled = !lp.isCameraEnabled;
  await lp.setCameraEnabled(enabled).catch((e) => log(e.message));
  $('#camBtn').textContent = `Камера: ${enabled ? 'вкл' : 'выкл'}`;
  $('#camBtn').classList.toggle('off', !enabled);
};

$('#screenBtn').onclick = async () => {
  const lp = state.room.localParticipant;
  const enabled = !lp.isScreenShareEnabled;
  try {
    await lp.setScreenShareEnabled(enabled, { audio: true });
    $('#screenBtn').classList.toggle('off', !enabled);
    log(enabled ? 'Демонстрация экрана включена' : 'Демонстрация экрана выключена');
  } catch (err) {
    log('Экран:', err.message);
  }
};

$('#recBtn').onclick = () => toggleRecording();
$('#recListBtn').onclick = () => showRecordings().catch((err) => log('Записи:', err.message));

$('#chatForm').addEventListener('submit', async (event) => {
  event.preventDefault();
  const text = $('#chatInput').value.trim();
  if (!text) return;
  $('#chatInput').value = '';
  await sendChat(text).catch((err) => log('Чат:', err.message));
});

$('#fileInput').onchange = async (event) => {
  const [file] = event.target.files;
  event.target.value = '';
  if (file) await uploadFile(file).catch((err) => log('Файл:', err.message));
};

$('#directFileInput').onchange = async (event) => {
  const [file] = event.target.files;
  event.target.value = '';
  if (file) await sendDirectFile(file).catch((err) => log('Файл:', err.message));
};
